import { useMemo } from 'react';
import type { PropertyResponseType } from '@services/housien/types';
import {
  aggregate,
  buildGroups,
  type BackendAggregationResponse,
  type GroupAggregate,
  type GroupBy,
  type PropertyGroup,
} from './property-grouping';

/** Re-exported so wiring code imports the backend contract from one place. */
export type { BackendAggregationResponse, BackendGroupAggregate } from './property-grouping';

interface PropertyGroupingData {
  /** The ordered groups for the active dimension. */
  groups: PropertyGroup[];
  /** Per-group aggregates; `null` for every group while metrics are pending. */
  aggregates: Map<string, GroupAggregate | null>;
  /** The portfolio total used for the summary cards' share. */
  total: number;
  /** True once the full filtered set is loaded and the metrics are final. */
  metricsReady: boolean;
}

interface UsePropertyGroupingDataArgs {
  /** The active grouping dimension (`none` short-circuits to empty). */
  group: GroupBy;
  /** The properties loaded so far (may be a partial page while scrolling). */
  properties: PropertyResponseType[];
  /** True once `page >= totalPages` — i.e. the full filtered set is in hand. */
  isComplete: boolean;
  /** The portfolio total count (from the list endpoint), when known. */
  totalCount: number | null;
  /** Translator for category / business / status group labels. */
  t: (key: string) => string;
}

/**
 * Derives the grouped view's data from the already-loaded property list.
 *
 * This is the SHIPPABLE client-side path: it partitions the loaded rows into
 * groups for the header labels and counts, but only computes the medians /
 * averages once the FULL filtered set is loaded (`isComplete`). While the list
 * is still paging in, every group's aggregate is `null` so the UI renders "—"
 * rather than a median taken from a partial page (which would be wrong).
 *
 * TODO(property-aggregation): once the backend `feat/property-aggregation-rich-stats`
 * endpoint lands and `openapi3-specification.yml` is regenerated via
 * `npm run generate:api`, replace the client-side aggregate below with a
 * TanStack Query call to the generated client, keyed on the active filter key:
 *   `PropertiesService.aggregateProperties({ agencyId, groupBy: group, stats: 'rich', ...filters })`
 * mapping each returned {@link BackendAggregationResponse} group through
 * `fromBackendAggregate`. The client-side path then becomes the offline/partial
 * fallback only. The response type ({@link BackendAggregationResponse}) is
 * already defined so this swap is a drop-in.
 *
 * @param args - The dimension, loaded rows, completeness flag, total, and translator.
 * @returns The grouped view data.
 */
export function usePropertyGroupingData(args: UsePropertyGroupingDataArgs): PropertyGroupingData {
  const { group, properties, isComplete, totalCount, t } = args;

  return useMemo(() => {
    if (group === 'none') {
      return { groups: [], aggregates: new Map(), total: totalCount ?? properties.length, metricsReady: false };
    }

    const groups = buildGroups(group, properties, t);
    const aggregates = new Map<string, GroupAggregate | null>();
    for (const g of groups) {
      aggregates.set(g.key, isComplete ? aggregate(g.items) : null);
    }

    return {
      groups,
      aggregates,
      total: totalCount ?? properties.length,
      metricsReady: isComplete,
    };
    // `t` is stable per language; excluded to avoid re-grouping on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [group, properties, isComplete, totalCount]);
}
